import { Link, NavLink, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { useEffect, useRef, useState } from "react";
import { PhoneCall, Sun, Moon, Menu, X, ChevronDown } from "lucide-react";
import { useBrand, useProductName, useSlogan } from "@/context/BrandContext";
import ViciBotsWidget from "@/components/marketing/ViciBotsWidget";

/** Primary links only — keeps the bar light. */
const PRIMARY_NAV = [
  { to: "/features", label: "Features" },
  { to: "/benefits", label: "Benefits" },
  { to: "/knowledge-base", label: "Knowledge Base" },
];

const AGENTS_NAV = [
  { to: "/agents/voice", label: "Voice Agents", key: "voice" },
  { to: "/agents/ai", label: "AI Agents", key: "ai" },
  { to: "/agents/hybrid", label: "Hybrid Agents", key: "hybrid" },
];

/** Secondary destinations tucked under More. */
const MORE_NAV = [
  { to: { pathname: "/", hash: "pricing" }, label: "Pricing", section: true },
  { to: { pathname: "/", hash: "ai-recordings" }, label: "AI Recordings", section: true },
  { to: { pathname: "/", hash: "faq" }, label: "FAQ", section: true },
];

/** After route change to /#section, scroll once the landing section exists. */
function useMarketingHashScroll() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (pathname !== "/" || !hash) return undefined;
    const id = decodeURIComponent(hash.replace(/^#/, ""));
    if (!id) return undefined;
    let cancelled = false;
    let attempt = 0;
    const run = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (attempt++ < 80) window.setTimeout(run, 40);
    };
    const t = window.setTimeout(run, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [pathname, hash]);
}

export function ThemeSelect({ className = "" }) {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const isDark = mounted && (resolvedTheme === "dark" || theme === "dark");

  return (
    <button
      type="button"
      data-testid="theme-toggle"
      onClick={() => setTheme(isDark ? "light" : "dark")}
      className={`grid h-9 w-9 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground ${className}`}
      aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
      title={isDark ? "Light mode" : "Dark mode"}
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

function NavDropdown({ testId, label, items, hot, onNavigate, align = "left" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={testId}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 transition-colors hover:text-foreground ${
          hot ? "font-medium text-foreground" : ""
        }`}
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          role="menu"
          className={`absolute top-full z-50 mt-2 min-w-[12.5rem] rounded-xl border border-border bg-card p-1.5 shadow-lg ${
            align === "right" ? "right-0" : "left-0"
          }`}
          data-testid={`${testId}-menu`}
        >
          {items.map((item) => (
            <Link
              key={item.label}
              role="menuitem"
              to={item.to}
              onClick={() => {
                setOpen(false);
                onNavigate?.();
              }}
              className="block rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MarketingShell({ children, active = "home" }) {
  const { brand } = useBrand();
  const productName = useProductName();
  const slogan = useSlogan();
  const [mobileOpen, setMobileOpen] = useState(false);
  useMarketingHashScroll();

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  const activeKey = (item) =>
    item.to === "/knowledge-base" ? "knowledge-base" : item.label.toLowerCase();

  const { pathname } = useLocation();
  const agentsActive =
    active === "agents" ||
    active === "voice" ||
    active === "ai" ||
    active === "hybrid" ||
    pathname.startsWith("/agents");

  const renderPrimary = (item, { mobile = false } = {}) => (
    <NavLink
      key={item.label}
      to={item.to}
      onClick={closeMobile}
      className={({ isActive }) => {
        const hot = isActive || active === activeKey(item);
        if (mobile) {
          return `rounded-lg px-3 py-2.5 text-sm transition-colors ${
            hot
              ? "bg-primary/10 font-semibold text-foreground"
              : "text-muted-foreground hover:bg-muted hover:text-foreground"
          }`;
        }
        return `transition-colors hover:text-foreground ${
          hot ? "font-medium text-foreground" : ""
        }`;
      }}
    >
      {item.label}
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-background text-foreground antialiased marketing-shell">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800&family=DM+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
        .marketing-shell { font-family: 'DM Sans', system-ui, sans-serif; }
        .marketing-shell .disp { font-family: 'Outfit', sans-serif; }
        @keyframes m-rise { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes m-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        .m-rise { animation: m-rise .65s ease both; }
        .m-float { animation: m-float 4.5s ease-in-out infinite; }
      `}</style>

      <header className="sticky top-0 z-50 border-b border-border/70 bg-background/90 backdrop-blur-xl">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-4 sm:px-6">
          <Link
            to="/"
            className="flex min-w-0 shrink-0 items-center gap-2.5"
            data-testid="marketing-brand"
            onClick={closeMobile}
          >
            {brand.logo_url ? (
              <img src={brand.logo_url} alt={productName || "Logo"} className="h-8 w-8 shrink-0 object-contain" />
            ) : brand.pending || !productName ? (
              <div className="h-8 w-8 shrink-0 rounded-lg bg-muted animate-pulse" aria-hidden />
            ) : (
              <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary text-primary-foreground">
                <PhoneCall className="h-4 w-4" />
              </div>
            )}
            {productName ? (
              <span className="min-w-0 leading-tight">
                <span className="disp block truncate text-[15px] font-bold tracking-tight">{productName}</span>
                {slogan ? (
                  <span
                    className="mt-0.5 block truncate text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
                    data-testid="marketing-slogan"
                  >
                    {slogan}
                  </span>
                ) : null}
              </span>
            ) : (
              <span className="inline-flex flex-col gap-1" aria-hidden>
                <span className="h-3.5 w-20 rounded bg-muted animate-pulse" />
                <span className="h-2 w-16 rounded bg-muted animate-pulse" />
              </span>
            )}
          </Link>

          <nav className="ml-6 hidden items-center gap-5 text-sm text-muted-foreground lg:flex">
            <NavDropdown
              testId="nav-agents"
              label="Agents"
              items={AGENTS_NAV}
              hot={agentsActive}
              onNavigate={closeMobile}
            />
            {PRIMARY_NAV.map((item) => renderPrimary(item))}
            <NavDropdown
              testId="nav-more"
              label="More"
              items={MORE_NAV}
              onNavigate={closeMobile}
            />
          </nav>

          <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2">
            <div className="hidden sm:block">
              <ViciBotsWidget variant="header" />
            </div>
            <ThemeSelect />
            <Link
              to="/login"
              data-testid="nav-signin"
              className="hidden text-sm text-muted-foreground transition-colors hover:text-foreground md:inline"
            >
              Sign in
            </Link>
            <Link
              to="/signup"
              data-testid="nav-get-started"
              className="hidden rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5 md:inline-flex"
            >
              Get started
            </Link>
            <button
              type="button"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-foreground transition-colors hover:bg-muted lg:hidden"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              data-testid="marketing-mobile-menu"
              onClick={() => setMobileOpen((v) => !v)}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div
            className="border-t border-border bg-background shadow-lg lg:hidden"
            data-testid="marketing-mobile-nav"
          >
            <nav className="mx-auto flex max-w-7xl flex-col gap-0.5 px-4 py-3 sm:px-6">
              <Link
                to="/"
                onClick={closeMobile}
                className="rounded-lg px-3 py-2.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                Home
              </Link>
              <p className="px-3 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Agents
              </p>
              {AGENTS_NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={closeMobile}
                  className={`rounded-lg px-3 py-2.5 text-sm transition-colors ${
                    pathname === item.to
                      ? "bg-primary/10 font-semibold text-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              {PRIMARY_NAV.map((item) => renderPrimary(item, { mobile: true }))}
              {MORE_NAV.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={closeMobile}
                  className="rounded-lg px-3 py-2.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  {item.label}
                </Link>
              ))}
              <div className="mt-2 sm:hidden">
                <ViciBotsWidget variant="header" />
              </div>
              <div className="mt-2 flex flex-col gap-2 border-t border-border pt-3 md:hidden">
                <Link
                  to="/login"
                  onClick={closeMobile}
                  className="rounded-lg px-3 py-2.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  Sign in
                </Link>
                <Link
                  to="/signup"
                  onClick={closeMobile}
                  className="rounded-full bg-primary px-3 py-2.5 text-center text-sm font-semibold text-primary-foreground"
                >
                  Get started
                </Link>
              </div>
            </nav>
          </div>
        )}
      </header>

      <main>{children}</main>

      <footer className="mt-8 border-t border-border">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-6 py-8 text-sm text-muted-foreground sm:flex-row">
          <div className="flex items-center gap-2">
            {brand.logo_url ? (
              <img src={brand.logo_url} alt="" className="h-6 w-6 object-contain" />
            ) : productName ? (
              <div className="grid h-6 w-6 place-items-center rounded-md bg-primary text-primary-foreground">
                <PhoneCall className="h-3.5 w-3.5" />
              </div>
            ) : (
              <div className="h-6 w-6 rounded-md bg-muted animate-pulse" aria-hidden />
            )}
            {productName ? (
              <span>{productName}</span>
            ) : (
              <span className="inline-block h-4 w-24 rounded bg-muted animate-pulse" aria-hidden />
            )}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            <Link to="/agents/voice" className="hover:text-foreground">Voice Agents</Link>
            <Link to="/agents/ai" className="hover:text-foreground">AI Agents</Link>
            <Link to="/agents/hybrid" className="hover:text-foreground">Hybrid Agents</Link>
            <Link to="/features" className="hover:text-foreground">Features</Link>
            <Link to="/benefits" className="hover:text-foreground">Benefits</Link>
            <Link to="/knowledge-base" className="hover:text-foreground">Knowledge Base</Link>
            <Link to={{ pathname: "/", hash: "pricing" }} className="hover:text-foreground">Pricing</Link>
            <Link to={{ pathname: "/", hash: "ai-recordings" }} className="hover:text-foreground">AI Recordings</Link>
            <Link to={{ pathname: "/", hash: "faq" }} className="hover:text-foreground">FAQ</Link>
            <Link to="/login" className="hover:text-foreground">Sign in</Link>
          </div>
          <p className="text-xs">© {new Date().getFullYear()} {productName || "\u00a0"}</p>
        </div>
      </footer>
    </div>
  );
}
